import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { User } from '@app/shared/models/user.interface';
import {
  ManagingCompanyAuthorityType,
  UserManagingCompanyAuthorities
} from '@app/shared/models/managing-company-authorities.interface';

@Component({
  selector: 'app-managing-company-user-edit-dialog',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatSelectTrigger
  ],
  templateUrl: './managing-company-user-edit-dialog.component.html',
  styleUrl: './managing-company-user-edit-dialog.component.scss'
})
export class ManagingCompanyUserEditDialogComponent {
  form: FormGroup;

  readonly authorityOptions = [ManagingCompanyAuthorityType.MANAGE_GLOBAL_AUTHORITY_REQUEST];

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: ManagingCompanyUserEditDialogData,
    private dialogRef: MatDialogRef<ManagingCompanyUserEditDialogComponent>
  ) {
    this.form = this.fb.group({
      managingCompanyAuthorities: [this.getSelectedAuthorities()]
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const selectedAuthorities = this.form.get('managingCompanyAuthorities')?.value ?? [];

    const existingAuthoritiesForCurrentManagingCompany =
      this.data.editedUser.managingCompanyAuthorities?.find(
        (authority) => authority.managingCompanyId === this.data.managingCompanyId
      )?.authorities ?? [];

    const preservedHiddenAuthorities = existingAuthoritiesForCurrentManagingCompany.filter(
      (authority) => authority === ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN
    );

    const finalAuthorities = [...new Set([...preservedHiddenAuthorities, ...selectedAuthorities])];

    const remainingAuthorities = (this.data.editedUser.managingCompanyAuthorities ?? []).filter(
      (authority) => authority.managingCompanyId !== this.data.managingCompanyId
    );

    const updatedAuthorities: UserManagingCompanyAuthorities[] =
      finalAuthorities.length > 0
        ? [
            ...remainingAuthorities,
            {
              userId: this.data.editedUser.id,
              managingCompanyId: this.data.managingCompanyId,
              authorities: finalAuthorities
            }
          ]
        : remainingAuthorities;

    this.dialogRef.close({
      ...this.data.editedUser,
      managingCompanyAuthorities: updatedAuthorities
    });
  }

  getSelectedAuthorities(): ManagingCompanyAuthorityType[] {
    const currentAuthorities =
      this.data.editedUser.managingCompanyAuthorities?.find(
        (authority) => authority.managingCompanyId === this.data.managingCompanyId
      )?.authorities ?? [];

    return currentAuthorities.filter(
      (authority) => authority === ManagingCompanyAuthorityType.MANAGE_GLOBAL_AUTHORITY_REQUEST
    );
  }

  getManagingCompanyAuthoritiesLabel(): string {
    const selectedAuthorities = this.form.get('managingCompanyAuthorities')?.value ?? [];
    return selectedAuthorities.join(', ');
  }

  compareAuthority = (a: ManagingCompanyAuthorityType, b: ManagingCompanyAuthorityType): boolean => a === b;

  protected readonly ManagingCompanyAuthorityType = ManagingCompanyAuthorityType;
}

export interface ManagingCompanyUserEditDialogData {
  editedUser: User;
  currentUser: User;
  managingCompanyId: number;
}
